import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Loader2,
  Play,
  Square,
  Save,
  RotateCcw,
  Sparkles,
  Plus,
  Trash2,
  Edit2,
  Check,
  X,
  Search,
  ExternalLink,
  CheckCircle,
  AlertCircle,
} from 'lucide-react'

import { sourcesApi, scrapersApi, SearchConfig } from '@/services/api'
import { useOrgStore } from '@/stores/org-store'
import { useSetBreadcrumbLabel } from '@/contexts/BreadcrumbContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

type FeedbackMessage = { type: 'success' | 'error'; text: string } | null

interface Source {
  id: number
  name: string
  url?: string
  source_type?: string
  scraper_status?: string
  search_config?: SearchConfig
}

const TERM_SPLIT_REGEX = /[,\n;\t|]+/

function normalizeTerms(input: unknown): string[] {
  if (typeof input === 'string') {
    input = [input]
  }
  if (!Array.isArray(input)) {
    return []
  }

  const normalized: string[] = []
  const seen = new Set<string>()
  for (const item of input) {
    if (typeof item !== 'string') continue
    for (const part of item.split(TERM_SPLIT_REGEX)) {
      const term = part.trim().toLowerCase()
      if (!term || seen.has(term)) continue
      seen.add(term)
      normalized.push(term)
    }
  }
  return normalized
}

function normalizeFilters(input: unknown): Record<string, unknown> {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return {}
  }
  return input as Record<string, unknown>
}

function stringifyFilters(input: unknown): string {
  return JSON.stringify(normalizeFilters(input), null, 2)
}

const STATUS_LABELS: Record<string, string> = {
  suggested: 'Suggested',
  analyzing: 'Analyzing',
  ready: 'Ready',
  working: 'Working',
  stopped: 'Stopped',
  error: 'Error',
}

const GEMI_ACCOUNTANT_PRESET_TERMS = [
  'real estate',
  'technology',
  'hospitality',
  'e-commerce',
]

const GEMI_ACCOUNTANT_PRESET_GREEK_ALIASES = [
  'ακίνητα',
  'τεχνολογία',
  'τουρισμός',
  'ηλεκτρονικό εμπόριο',
]

const GEMI_FILTER_KEY_HELP = [
  'status_ids',
  'legal_type_ids',
  'suspension_ids',
  'special_characteristic_ids',
  'armodia_gemi_ids',
  'kad_node_ids',
  'place_node_ids',
  'city',
  'postcode',
  'recommendation_date_from',
  'recommendation_date_to',
  'closing_date_from',
  'closing_date_to',
  'alteration_date_from',
  'alteration_date_to',
  'radio_value',
]

function getStatusVariant(
  status?: string
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status?.toLowerCase()) {
    case 'working':
    case 'ready':
      return 'default'
    case 'suggested':
    case 'analyzing':
      return 'secondary'
    case 'error':
      return 'destructive'
    case 'stopped':
      return 'outline'
    default:
      return 'outline'
  }
}

export default function SourceConfigPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const org = useOrgStore((s) => s.currentOrg)
  const canManage = useMemo(
    () => org?.role === 'owner' || org?.role === 'admin',
    [org?.role]
  )
  const setBreadcrumbLabel = useSetBreadcrumbLabel()

  const sourceId = Number(id)
  const isValidSourceId = Number.isInteger(sourceId) && sourceId > 0

  const { data: sources, isLoading } = useQuery<Source[]>({
    queryKey: ['sources'],
    queryFn: () => sourcesApi.list({ recheck: true }),
    enabled: isValidSourceId,
  })

  const source = useMemo(
    () => sources?.find((item) => item.id === sourceId) || null,
    [sources, sourceId]
  )

  const status = source?.scraper_status?.toLowerCase() || ''
  const isGemiSource = useMemo(() => {
    const name = (source?.name || '').toLowerCase()
    const url = (source?.url || '').toLowerCase()
    return (
      name.includes('gemi') ||
      name.includes('general commercial register') ||
      url.includes('businessportal.gr')
    )
  }, [source?.name, source?.url])
  const isSuggested = status === 'suggested'
  const isAnalyzing = status === 'analyzing'
  const canStartByStatus = status === 'ready' || status === 'stopped'
  const canStop = status === 'working'

  const [terms, setTerms] = useState<string[]>([])
  const [originalTerms, setOriginalTerms] = useState<string[]>([])
  const [maxPages, setMaxPages] = useState(50)
  const [originalMaxPages, setOriginalMaxPages] = useState(50)
  const [filtersText, setFiltersText] = useState('{}')
  const [originalFiltersText, setOriginalFiltersText] = useState('{}')
  const [newTerm, setNewTerm] = useState('')
  const [message, setMessage] = useState<FeedbackMessage>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [expandGemiPreset, setExpandGemiPreset] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editingValue, setEditingValue] = useState('')

  useEffect(() => {
    if (source?.name) {
      setBreadcrumbLabel('sourceName', source.name)
    }
    return () => setBreadcrumbLabel('sourceName', undefined)
  }, [source?.name, setBreadcrumbLabel])

  useEffect(() => {
    if (!source) return
    const config = source.search_config
    const sourceTerms = normalizeTerms(config?.terms || [])
    const pages = config?.max_pages || 50
    const normalizedFilters = stringifyFilters(config?.filters)

    setTerms(sourceTerms)
    setOriginalTerms(sourceTerms)
    setMaxPages(pages)
    setOriginalMaxPages(pages)
    setFiltersText(normalizedFilters)
    setOriginalFiltersText(normalizedFilters)
    setMessage(null)
    setEditingIndex(null)
    setEditingValue('')
    setNewTerm('')
    setExpandGemiPreset(false)
  }, [source])

  const { data: resolvedTermsData, isLoading: isLoadingResolved } = useQuery({
    queryKey: ['resolved-terms', source?.id],
    queryFn: () => sourcesApi.getResolvedTerms(source!.id),
    enabled: !!source?.id,
  })

  const resolvedTerms = resolvedTermsData?.terms || []
  const persistedFilters = normalizeFilters(source?.search_config?.filters)
  const hasPersistedFilters = Object.keys(persistedFilters).length > 0
  const canStart =
    canStartByStatus &&
    !isLoadingResolved &&
    (resolvedTerms.length > 0 || hasPersistedFilters)

  const hasChanges = useMemo(() => {
    return (
      JSON.stringify(terms) !== JSON.stringify(originalTerms) ||
      maxPages !== originalMaxPages ||
      filtersText.trim() !== originalFiltersText.trim()
    )
  }, [terms, originalTerms, maxPages, originalMaxPages, filtersText, originalFiltersText])

  const runScrapeMutation = useMutation({
    mutationFn: (sourceName: string) => scrapersApi.run(sourceName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sources'] })
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      setMessage({ type: 'success', text: 'Search started for this source.' })
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail || 'Failed to start source search.'
      setMessage({ type: 'error', text: detail })
    },
  })

  const stopScraperMutation = useMutation({
    mutationFn: (id: number) => sourcesApi.stop(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sources'] })
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      setMessage({ type: 'success', text: 'Search stopped for this source.' })
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail || 'Failed to stop source search.'
      setMessage({ type: 'error', text: detail })
    },
  })

  const startSearch = () => {
    if (!source) return
    runScrapeMutation.mutate(source.name)
  }

  const stopSearch = () => {
    if (!source) return
    stopScraperMutation.mutate(source.id)
  }

  const addTerm = () => {
    const parsedTerms = normalizeTerms(newTerm)
    if (parsedTerms.length === 0) return

    const merged = [...terms]
    let added = 0
    for (const term of parsedTerms) {
      if (!merged.includes(term)) {
        merged.push(term)
        added += 1
      }
    }

    if (added === 0) {
      setMessage({ type: 'error', text: 'All entered terms already exist.' })
      return
    }

    setTerms(merged)
    setNewTerm('')
    setMessage(null)
  }

  const removeTerm = (index: number) => {
    setTerms((prev) => prev.filter((_, i) => i !== index))
    setMessage(null)
  }

  const startEditing = (index: number) => {
    setEditingIndex(index)
    setEditingValue(terms[index])
  }

  const cancelEditing = () => {
    setEditingIndex(null)
    setEditingValue('')
  }

  const saveEditing = () => {
    if (editingIndex === null) return
    const trimmed = editingValue.trim()
    if (!trimmed) {
      setMessage({ type: 'error', text: 'Search term cannot be empty.' })
      return
    }
    if (trimmed !== terms[editingIndex] && terms.includes(trimmed)) {
      setMessage({ type: 'error', text: `"${trimmed}" already exists.` })
      return
    }
    setTerms((prev) => {
      const updated = [...prev]
      updated[editingIndex] = trimmed
      return updated
    })
    setEditingIndex(null)
    setEditingValue('')
    setMessage(null)
  }

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      saveEditing()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelEditing()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addTerm()
    }
  }

  const discardChanges = () => {
    setTerms([...originalTerms])
    setMaxPages(originalMaxPages)
    setFiltersText(originalFiltersText)
    setEditingIndex(null)
    setEditingValue('')
    setMessage(null)
  }

  const saveConfig = async () => {
    if (!source || !canManage || !hasChanges) return
    setIsSaving(true)
    setMessage(null)
    try {
      let parsedFilters: Record<string, unknown> = {}
      const trimmedFilters = filtersText.trim()
      if (trimmedFilters) {
        const parsed = JSON.parse(trimmedFilters)
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          parsedFilters = parsed as Record<string, unknown>
        } else {
          throw new Error('Filters JSON must be an object.')
        }
      }

      await sourcesApi.update(source.id, {
        search_config: {
          terms: normalizeTerms(terms),
          max_pages: maxPages,
          filters: parsedFilters,
        },
      })
      setOriginalTerms([...terms])
      setOriginalMaxPages(maxPages)
      const normalizedFiltersText = stringifyFilters(parsedFilters)
      setFiltersText(normalizedFiltersText)
      setOriginalFiltersText(normalizedFiltersText)
      setMessage({ type: 'success', text: 'Search configuration saved.' })
      queryClient.invalidateQueries({ queryKey: ['sources'] })
      queryClient.invalidateQueries({ queryKey: ['resolved-terms', source.id] })
    } catch (err: any) {
      const detail =
        err instanceof SyntaxError
          ? 'Invalid filters JSON. Please fix formatting and try again.'
          : err?.response?.data?.detail || err?.message || 'Failed to save configuration.'
      setMessage({ type: 'error', text: detail })
    } finally {
      setIsSaving(false)
    }
  }

  const applyGemiAccountantPreset = () => {
    if (!canManage) return

    let parsedFilters: Record<string, unknown> = {}
    try {
      const trimmed = filtersText.trim()
      if (trimmed) {
        const parsed = JSON.parse(trimmed)
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          parsedFilters = normalizeFilters(parsed)
        } else {
          throw new Error('Filters JSON must be an object.')
        }
      }
    } catch {
      setMessage({
        type: 'error',
        text: 'Cannot apply preset because filters JSON is invalid. Fix JSON first.',
      })
      return
    }

    const existingTerms = normalizeTerms(terms)
    const termsToMerge = [...GEMI_ACCOUNTANT_PRESET_TERMS]
    if (existingTerms.length === 0 && expandGemiPreset) {
      termsToMerge.push(...GEMI_ACCOUNTANT_PRESET_GREEK_ALIASES)
    }

    const mergedTerms = [...existingTerms]
    for (const term of normalizeTerms(termsToMerge)) {
      if (!mergedTerms.includes(term)) {
        mergedTerms.push(term)
      }
    }

    const nextFilters = {
      ...parsedFilters,
      status_ids: ['3'],
      radio_value: 'company',
    }

    setTerms(mergedTerms)
    setFiltersText(stringifyFilters(nextFilters))
    setMessage({
      type: 'success',
      text: 'Applied GEMI accountant preset. Save to persist these changes.',
    })
  }

  const generateTerms = async () => {
    if (!source || !canManage) return
    setIsGenerating(true)
    setMessage(null)
    try {
      const result = await sourcesApi.generateTerms(source.id)
      const generated = normalizeTerms(result.terms || [])
      const merged = [...normalizeTerms(terms)]
      let addedCount = 0
      for (const term of generated) {
        if (!merged.includes(term)) {
          merged.push(term)
          addedCount++
        }
      }
      setTerms(merged)
      setMessage({
        type: 'success',
        text: `Generated ${generated.length} terms, ${addedCount} new added. Save to persist.`,
      })
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Failed to generate search terms.'
      setMessage({ type: 'error', text: detail })
    } finally {
      setIsGenerating(false)
    }
  }

  if (!isValidSourceId) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground">Invalid source id.</p>
        </CardContent>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-40" />
        <Card>
          <CardContent className="p-6 space-y-3">
            <Skeleton className="h-7 w-64" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-56" />
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!source) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/app/sources')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Sources
        </Button>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Source not found.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <Button variant="ghost" className="px-2" onClick={() => navigate('/app/sources')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Sources
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Search Configuration</h1>
            <p className="mt-1 text-muted-foreground">
              Configure terms/filters and run search for <span className="font-medium text-foreground">{source.name}</span>.
            </p>
            {source.url && (
              <a
                href={source.url.startsWith('http') ? source.url : `https://${source.url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary hover:underline"
              >
                {source.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Hide "Ready" badge for accepted sources; keep status internally for start/stop logic */}
            {status !== 'ready' && (
              <Badge variant={getStatusVariant(source.scraper_status)}>
                {STATUS_LABELS[status] || source.scraper_status || 'Unknown'}
              </Badge>
            )}
            {source.source_type && <Badge variant="outline">{source.source_type}</Badge>}
          </div>
        </div>
        <div className="flex gap-2">
          {canStop ? (
            <Button
              variant="outline"
              onClick={stopSearch}
              disabled={stopScraperMutation.isPending}
            >
              {stopScraperMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Square className="mr-2 h-4 w-4" />
              )}
              Stop Search
            </Button>
          ) : (
            <Button
              onClick={startSearch}
              disabled={!canStart || runScrapeMutation.isPending}
            >
              {runScrapeMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              Start Search
            </Button>
          )}
        </div>
      </div>

      {isSuggested && (
        <Alert>
          <AlertDescription className="text-sm">
            This source is still suggested. Accept it from the{' '}
            <Link to="/app/sources" className="underline">
              Sources page
            </Link>{' '}
            before running a search.
          </AlertDescription>
        </Alert>
      )}

      {isAnalyzing && (
        <Alert>
          <AlertDescription className="text-sm">
            This source is currently analyzing. Start search when analysis is complete.
          </AlertDescription>
        </Alert>
      )}

      {!isSuggested &&
        !isAnalyzing &&
        canManage &&
        !isLoadingResolved &&
        resolvedTerms.length === 0 &&
        !hasPersistedFilters && (
        <Alert>
          <AlertDescription className="text-sm">
            Add source-specific search terms or filters before starting search.
          </AlertDescription>
        </Alert>
      )}

      {!canManage && (
        <Alert>
          <AlertDescription className="text-sm">
            Read-only access. Ask an owner or admin to manage this configuration.
          </AlertDescription>
        </Alert>
      )}

      {message && (
        <Alert variant={message.type === 'error' ? 'destructive' : 'default'}>
          {message.type === 'success' ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
          <AlertDescription>{message.text}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Source Search Settings</CardTitle>
          <CardDescription>
            Configure source-specific terms and controls used while searching this source.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="max-pages" className="text-sm font-medium">
              Max Pages per Search Term
            </Label>
            <Input
              id="max-pages"
              type="number"
              min={1}
              max={500}
              value={maxPages}
              onChange={(e) => setMaxPages(Math.max(1, Math.min(500, parseInt(e.target.value, 10) || 50)))}
              disabled={!canManage}
              className="w-32"
            />
            <p className="text-xs text-muted-foreground">
              Limits how many pages are scanned per term (1-500).
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Source-Specific Search Terms</Label>
              <span className="text-xs text-muted-foreground">
                {terms.length} term{terms.length !== 1 ? 's' : ''}
              </span>
            </div>

            {isGemiSource && canManage && (
              <div className="space-y-2 rounded-md border bg-muted/20 p-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-muted-foreground">
                    Apply an accountant starter preset: active companies + default sector terms.
                  </p>
                  <Button type="button" variant="outline" size="sm" onClick={applyGemiAccountantPreset}>
                    Apply GEMI Accountant Preset
                  </Button>
                </div>
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={expandGemiPreset}
                    onChange={(e) => setExpandGemiPreset(e.target.checked)}
                  />
                  Include Greek aliases only when there are currently no terms.
                </label>
              </div>
            )}

            {canManage && (
              <div className="flex gap-2">
                <Input
                  placeholder="Add a search term..."
                  value={newTerm}
                  onChange={(e) => setNewTerm(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="text-sm"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addTerm}
                  disabled={!newTerm.trim()}
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            )}

            {terms.length === 0 ? (
              <div className="rounded-md border bg-muted/30 py-5 text-center text-muted-foreground">
                <Search className="mx-auto mb-1 h-5 w-5 opacity-50" />
                <p className="text-xs">No source-specific terms.</p>
                <p className="mt-0.5 text-xs">Add terms or configure filters to run search for this source.</p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {terms.map((term, index) =>
                  editingIndex === index ? (
                    <div
                      key={`edit-${index}`}
                      className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2 py-1"
                    >
                      <Input
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        onKeyDown={handleEditKeyDown}
                        className="h-5 w-24 px-1 text-xs"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={saveEditing}
                        className="text-green-600 transition-colors hover:text-green-700"
                        aria-label="Save"
                      >
                        <Check className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        onClick={cancelEditing}
                        className="text-red-600 transition-colors hover:text-red-700"
                        aria-label="Cancel"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <Badge
                      key={`${term}-${index}`}
                      variant="secondary"
                      className="group gap-1 px-2 py-1 text-xs"
                    >
                      {term}
                      {canManage && (
                        <>
                          <button
                            type="button"
                            onClick={() => startEditing(index)}
                            className="ml-0.5 opacity-0 transition-opacity hover:text-primary group-hover:opacity-100"
                            aria-label={`Edit ${term}`}
                          >
                            <Edit2 className="h-2.5 w-2.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeTerm(index)}
                            className="ml-0.5 opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                            aria-label={`Remove ${term}`}
                          >
                            <Trash2 className="h-2.5 w-2.5" />
                          </button>
                        </>
                      )}
                    </Badge>
                  )
                )}
              </div>
            )}

            {canManage && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={generateTerms}
                disabled={isGenerating}
                className="w-full"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-3 w-3" />
                    Generate Terms for This Source
                  </>
                )}
              </Button>
            )}
          </div>

          <div className="space-y-2 border-t pt-4">
            <Label htmlFor="source-filters" className="text-sm font-medium">
              Source Filters (JSON)
            </Label>
            <p className="text-xs text-muted-foreground">
              Optional source-specific filters. Use this for per-source constraints such as status, legal type, region, query params, or form values.
            </p>
            {isGemiSource && (
              <p className="text-xs text-muted-foreground">
                GEMI filter keys: <code>{GEMI_FILTER_KEY_HELP.join(', ')}</code>
              </p>
            )}
            <Textarea
              id="source-filters"
              value={filtersText}
              onChange={(e) => setFiltersText(e.target.value)}
              disabled={!canManage}
              rows={10}
              className="font-mono text-xs"
              placeholder='{"query_params":{"status":"active"},"form_values":{"select[name=status]":"active"}}'
            />
          </div>

          <div className="space-y-2 border-t pt-4">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <Search className="h-4 w-4" />
              Effective Search Terms (Preview)
            </Label>
            <p className="text-xs text-muted-foreground">
              Terms that will be used when you start search for this source.
            </p>
            {isLoadingResolved ? (
              <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Loading resolved terms...
              </div>
            ) : resolvedTerms.length === 0 ? (
              <div className="py-2 text-xs text-muted-foreground">
                {hasPersistedFilters
                  ? 'No terms configured. Search will run using source filters only.'
                  : 'No terms will be used. Add source-specific terms or filters.'}
              </div>
            ) : (
              <div className="flex flex-wrap gap-1">
                {resolvedTerms.map((term: string, index: number) => (
                  <Badge key={`resolved-${term}-${index}`} variant="outline" className="text-xs">
                    {term}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {canManage && (
            <div className="flex items-center justify-end gap-2 border-t pt-4">
              {hasChanges && (
                <Button type="button" variant="ghost" size="sm" onClick={discardChanges}>
                  <RotateCcw className="mr-1 h-3 w-3" />
                  Discard
                </Button>
              )}
              <Button type="button" size="sm" onClick={saveConfig} disabled={isSaving || !hasChanges}>
                {isSaving ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <Save className="mr-1 h-3 w-3" />
                )}
                Save
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
